import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import eggBaby from "../assets/img/egg_baby.webp";
import babyAdult from "../assets/img/baby_dragon.webp";
import xpDragon from "../assets/img/xp_dragon.webp";
import rpgDragon from "../assets/img/battle_rpg.webp";

export default function PPDSection() {
  return (
    <div>
      <TextParallaxContent
        imgUrl={eggBaby}
        subheading="Discover our new web3 experience"
        heading="PolarFi Pet Dragons"
      >
        <ExampleContent
          title="Mint Your Dragon Egg"
          description="The journey begins with a single egg. Each one is a unique on-chain asset carrying hidden traits, elemental affinity, and rarity. Choose wisely — your dragon's destiny starts the moment you mint."
          buttonText="Mint an Egg"
          buttonUrl="https://ppd.polarfi.io"
        />
      </TextParallaxContent>

      <TextParallaxContent
        imgUrl={xpDragon}
        subheading="Evolution"
        heading="Hatch and Nurture"
      >
        <ExampleContent
          title="Raise Your Baby Dragon"
          description="After hatching, your dragon becomes a living Web3 companion. Feed it, train it, level up its stats, and shape its identity. Every action you take boosts specific attributes and unlocks new evolutionary paths."
          hideButton
        />
      </TextParallaxContent>

      <TextParallaxContent
        imgUrl={babyAdult}
        subheading="Unlock Potential"
        heading="Forge a Legendary Dragon"
      >
        <ExampleContent
          title="Evolve Through On-Chain Choices"
          description="Your dragon can evolve into over 70 unique adult forms. Evolution chances are fully stat-driven: the attributes you prioritized — strength, agility, elemental mastery, resilience — directly influence your probabilities of unlocking rare and mythical species."
          hideButton
        />
      </TextParallaxContent>

      <TextParallaxContent
        imgUrl={rpgDragon}
        subheading="Immersion"
        heading="Enter the Polar RPG"
      >
        <ExampleContent
          title="Fight, Explore, Conquer"
          description="Bring your dragon into the Polar RPG universe and let it fight at your side. Every battle, quest, and victory strengthens your bond and upgrades your on-chain stats. Your dragon isn't just a collectible — it's your loyal partner in a living Web3 adventure."
          buttonText="Enter Polar RPG"
          buttonUrl="https://rpg.polarfi.site"
          noExtraPadding // <-- Retire md:pb-80 pour cette section
        />
      </TextParallaxContent>
    </div>
  );
}

// Composants auxiliaires

const TextParallaxContent = ({ imgUrl, subheading, heading, children }) => {
  return (
    <div className="relative">
      <div className="relative h-[120vh]">
        <StickyImage imgUrl={imgUrl} />
        <OverlayCopy heading={heading} subheading={subheading} />
      </div>
      {children}
    </div>
  );
};

const StickyImage = ({ imgUrl }) => {
  const targetRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: targetRef,
    offset: ["end end", "end start"],
  });

  const scale = useTransform(scrollYProgress, [0, 1], [1, 0.85]);
  const opacity = useTransform(scrollYProgress, [0, 1], [1, 0]);

  return (
    <motion.div
      style={{
        backgroundImage: `url(${imgUrl})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        height: "100vh",
        top: 0,
        scale,
      }}
      ref={targetRef}
      className="sticky z-0 overflow-hidden"
    >
      <motion.div
        className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/70 to-black/90"
        style={{ opacity }}
      />
    </motion.div>
  );
};

const OverlayCopy = ({ subheading, heading }) => {
  const targetRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: targetRef,
    offset: ["start end", "end start"],
  });

  const y = useTransform(scrollYProgress, [0, 1], [250, -250]);
  const opacity = useTransform(scrollYProgress, [0.25, 0.5, 0.75], [0, 1, 0]);

  return (
    <motion.div
      style={{ y, opacity }}
      ref={targetRef}
      className="absolute left-0 top-0 flex h-screen w-full flex-col items-center justify-center text-white pointer-events-none px-4"
    >
      <motion.p
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="mb-2 text-center text-lg md:mb-4 md:text-2xl font-medium tracking-wider uppercase text-cyan-400"
      >
        {subheading}
      </motion.p>
      <motion.p
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.2 }}
        className="text-center text-4xl font-bold md:text-7xl lg:text-8xl max-w-5xl leading-tight"
      >
        {heading}
      </motion.p>
    </motion.div>
  );
};

const ExampleContent = ({
  title,
  description,
  buttonText,
  buttonUrl,
  hideButton,
  noExtraPadding, // nouvelle prop
}) => (
  <div
    className={`mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 pb-32 ${
      !noExtraPadding ? "md:pb-80" : ""
    }`}
  >
    <div className="grid grid-cols-1 gap-8 md:grid-cols-12 md:gap-12 items-start">
      <motion.h2
        initial={{ opacity: 0, x: -20 }}
        whileInView={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.6 }}
        viewport={{ once: true }}
        className="col-span-1 text-3xl md:text-4xl lg:text-5xl font-bold text-white md:col-span-4 leading-tight"
      >
        {title}
      </motion.h2>

      <motion.div
        initial={{ opacity: 0, x: 20 }}
        whileInView={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.6, delay: 0.2 }}
        viewport={{ once: true }}
        className="col-span-1 md:col-span-8 space-y-6"
      >
        <p className="text-lg md:text-xl lg:text-2xl text-slate-300 leading-relaxed">
          {description}
        </p>

        {!hideButton && buttonText && buttonUrl && (
          <a href={buttonUrl} target="_blank" rel="noopener noreferrer">
            <button
              name="dapp_button"
              className="text-white bg-sky-500 h-10 px-5 mt-8 rounded-lg transition-transform transform hover:scale-105 hover:bg-sky-600 hover:shadow-lg"
            >
              {buttonText}
            </button>
          </a>
        )}
      </motion.div>
    </div>
  </div>
);
